import React from "react";
import type { MenuProps } from "antd";
import { Menu } from "antd";
import Image from "next/image";
import Link from "next/link";

import { analytics } from "@flare/analytics";
import { useTranslation } from "@flare/i18n";
import { Avatar, Button, cn } from "@flare/ui";

import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/dropdown";
import {
  MarbleHomeButton,
  PersonalInfoButton,
  SignoutButton,
} from "@/modules/pre-client/components/navbar-menu-buttons";
import { SIGNIN_PAGE_URL } from "@/modules/pre-client/pre-client.consts";
import { BiEventsNames } from "@/services/analytics/bi-event-names";
import { useAppContext } from "@/services/store/app-context";

import type { NavbarProps } from "../NavbarV2.types";

export const desktopNavbarStyles = {
  display: "flex",
  justifyContent: "center",
  lineHeight: "inherit",
  alignItems: "center",
  height: "100%",
  backgroundColor: "transparent",
  border: "none",
  width: "100%",
};

export function DesktopNavbar({
  items,
  isSignedIn,
  userInitials,
  onMarbleHomeClick,
  onPersonalInfoClick,
  onSignoutClick,
  onSigninClick,
  onFunnelCtaClick,
  preClientAbTest,
}: NavbarProps) {
  const { t } = useTranslation();
  const {
    navbar: { selectedNavbarItem, setSelectedNavbarItem },
  } = useAppContext();

  const signinURL = preClientAbTest.isOn
    ? SIGNIN_PAGE_URL
    : process.env.NEXT_PUBLIC_CUSTOMERS_APP_URL || "";

  const menuItemClickHandler: MenuProps["onClick"] = (e) => {
    setSelectedNavbarItem(e.key);
  };

  function dropdownTriggerClickHandler() {
    analytics.track(BiEventsNames.WebPageClick, {
      location: "navbar",
      element_name: "menu",
      click_type: "click",
    });
  }

  return (
    <nav className="bg-surface pl-sp-7 pr-sp-6">
      <div className="mx-auto flex h-[71px] max-w-[1440px] items-center justify-between">
        <Link href="/">
          <Image
            width={97}
            height={23.95}
            src="/logos/marble-logo.svg"
            alt={t("MARBLE_LOGO_ALT")}
          />
        </Link>

        <div
          className={cn("w-3/5", {
            invisible: !preClientAbTest.isReady,
          })}
        >
          <Menu
            onClick={menuItemClickHandler}
            selectedKeys={[selectedNavbarItem]}
            mode="horizontal"
            items={items}
            style={desktopNavbarStyles}
          />
        </div>

        <div className={cn({ invisible: !preClientAbTest.isReady })}>
          {isSignedIn ? (
            <DropdownMenu>
              <DropdownMenuTrigger
                asChild
                onClick={dropdownTriggerClickHandler}
              >
                <Avatar
                  size="md"
                  fallback={userInitials || ""}
                  className="cursor-pointer"
                />
              </DropdownMenuTrigger>
              <DropdownMenuContent
                className="w-64 rounded-lg bg-surface shadow-md"
                side="bottom"
                align="end"
                sideOffset={25}
              >
                <DropdownMenuItem>
                  <MarbleHomeButton onClick={onMarbleHomeClick!} />
                </DropdownMenuItem>
                <DropdownMenuItem>
                  <PersonalInfoButton onClick={onPersonalInfoClick!} />
                </DropdownMenuItem>
                <DropdownMenuItem>
                  <SignoutButton onClick={onSignoutClick!} />
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          ) : (
            <div className="flex items-center gap-sp-8">
              <Link onClick={onSigninClick} href={signinURL}>
                {t("SIGN_IN")}
              </Link>

              <Button size="md" asChild>
                <Link onClick={onFunnelCtaClick} href="/v2/funnel">
                  {t("GET_STARTED")}
                </Link>
              </Button>
            </div>
          )}
        </div>
      </div>
    </nav>
  );
}
